import { Temporal } from '@js-temporal/polyfill';
import { de as deLocale, enGB as enLocale, fr as frLocale, nl as nlLocale } from 'date-fns/locale';
import isNil from 'lodash/isNil';

import { getTypedLanguage } from './i18nutils';
import { Language } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CalendarDate = Temporal.PlainDate;
export type DateTime = Temporal.Instant;
export interface Interval {
  start: CalendarDate;
  end: CalendarDate;
}
export interface DateTimeInterval {
  start: DateTime;
  end: DateTime;
}
type CalendarDateDurationUnit = 'days' | 'months' | 'years';
export type DateTimeDurationUnit = 'seconds' | 'minutes' | 'hours';
type CalendarDateField = 'day' | 'month' | 'year';
type CalendarMonthFormat = 'numeric' | 'short' | 'long';

/**
 * Branded wire-format string for `AWSDate` (`YYYY-MM-DD`).
 *
 * Use anywhere a calendar-date value crosses an I/O boundary (GraphQL payload,
 * DynamoDB attribute, form submit). Inside the codebase, prefer `CalendarDate`.
 */
type CalendarDateISO = string & { readonly __brand: 'CalendarDateISO' };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AWS_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const HOURS_IN_DAY = 24;
export const ONE_DAY_IN_SECONDS = HOURS_IN_DAY * 60 * 60;
export const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;
export const MIN_DATE: CalendarDate = Temporal.PlainDate.from('1900-01-01');
export const MAX_DATE: CalendarDate = Temporal.PlainDate.from('9999-12-31');
export const EPOCH_TIMESTAMP = '1970-01-01T00:00:00.000Z';
export const DAY_START_TIME = 'T00:00:00.000Z';
export const DAY_END_TIME = 'T23:59:59.999Z';

export const MONTHS_IN_QUARTER = 3;
export const MONTHS_IN_SEMESTER = 6;
export const MONTHS_IN_YEAR = 12;
const LAST_BUSINESS_DAY_OF_WEEK = 5;
const BUSINESS_DAYS_PER_WEEK = 5;
const WEEKEND_DAYS_PER_WEEK = 2;

// ---------------------------------------------------------------------------
// Locale
// ---------------------------------------------------------------------------

const LOCALE_BY_LANGUAGE: Record<Language, Locale> = {
  [Language.FR]: frLocale,
  [Language.NL]: nlLocale,
  [Language.EN]: enLocale,
  [Language.DE]: deLocale,
} as const;

export const getLocaleFromLanguage = (languageStr: string | Language): Locale => {
  const language = getTypedLanguage(languageStr);
  return LOCALE_BY_LANGUAGE[language];
};

// ---------------------------------------------------------------------------
// CalendarDate — read boundary (parse / construct)
// ---------------------------------------------------------------------------

/**
 * Narrows a raw `string` (typically from GraphQL codegen) to `CalendarDateISO`.
 * Throws when the input is not exactly `YYYY-MM-DD` or is not a real calendar date.
 */
const asCalendarDateISO = (value: string): CalendarDateISO => {
  if (!AWS_DATE_REGEX.test(value)) {
    throw new Error(`Expected AWSDate (YYYY-MM-DD), got "${value}"`);
  }
  Temporal.PlainDate.from(value); // throws RangeError on invalid dates like 2026-13-99
  return value as CalendarDateISO;
};

/**
 * Parse a raw GraphQL / DynamoDB string to a `CalendarDate`, handling `null` / `undefined`.
 * Use this at every read boundary instead of composing `toCalendarDate(asCalendarDateISO(...))`.
 * Returns `null` for absent values; throws on malformed input.
 */
export const toCalendarDate = (value: string): CalendarDate => Temporal.PlainDate.from(asCalendarDateISO(value));

export const toCalendarDateNullable = (value: string | null | undefined): CalendarDate | null => {
  if (!value) return null;
  return toCalendarDate(value);
};
export const toCalendarDateFromParts = (year: number, month: number, day: number): CalendarDate =>
  Temporal.PlainDate.from({ year, month, day });

export const createCalendarDate = (year: number, month: number, day: number): CalendarDate =>
  Temporal.PlainDate.from({ year, month, day });

// ---------------------------------------------------------------------------
// CalendarDate — write boundary (serialize to wire)
// ---------------------------------------------------------------------------

/** Inverse of `toCalendarDate` — serialize a `CalendarDate` to its wire-format string. */
export const fromCalendarDate = (date: CalendarDate): CalendarDateISO => date.toString() as CalendarDateISO;

// ---------------------------------------------------------------------------
// DateTime — parse / serialize / convert
// ---------------------------------------------------------------------------

export const fromDateTime = (dateTime: DateTime): string => dateTime.toString({ fractionalSecondDigits: 3 });
export const fromDateTimeNullable = (value?: string | null): string => value ?? fromDateTime(todayDateTime());

export const fromDateTimeToSeconds = (dateTime: DateTime): number => Math.floor(dateTime.epochMilliseconds / 1000);

export const getEpochSeconds = (dateTime: DateTime): number => Math.floor(dateTime.epochMilliseconds / 1000);

export const getEpochMilliFromNow = (): number => todayDateTime().epochMilliseconds;

export const calendarDateTimeFromTimestamp = (timestamp: string): DateTime => Temporal.Instant.from(timestamp);

const DEFAULT_TIME_ZONE = 'Europe/Brussels';

export const calendarDateFromInstant = (instant: DateTime, timeZone: string = DEFAULT_TIME_ZONE): CalendarDate =>
  instant.toZonedDateTimeISO(timeZone).toPlainDate();

export const calendarDateFromTimestamp = (timestamp: string, timeZone: string = DEFAULT_TIME_ZONE): CalendarDate =>
  calendarDateFromInstant(calendarDateTimeFromTimestamp(timestamp), timeZone);

export const dateTimeFromEpochMilliseconds = (epochMilliseconds: number): DateTime =>
  Temporal.Instant.fromEpochMilliseconds(epochMilliseconds);

export const dateTimeFromEpochSeconds = (epochSeconds: number): DateTime =>
  dateTimeFromEpochMilliseconds(epochSeconds * 1000);

// ---------------------------------------------------------------------------
// MUI date-picker bridge — NEVER USE OUTSIDE DATE PICKER
// ---------------------------------------------------------------------------

/*NEVER USE OUTSIDE DATE PICKER */
/**
 * Bridges `CalendarDate` with invoice date fields still typed `AWSDateTime` (not yet migrated to
 * `AWSDate`) — reads the UTC calendar day out of a full ISO datetime string. Drop once those fields
 * become `AWSDate` and switch to `toCalendarDate`.
 */
export const calendarDateFromAwsDateTime = (value: string): CalendarDate => Temporal.PlainDate.from(value.slice(0, 10));

/** Inverse of `calendarDateFromAwsDateTime` — serializes to midnight UTC on that calendar day. */
export const calendarDateToAwsDateTimeMidnightUtc = (date: CalendarDate): string => `${date.toString()}T00:00:00.000Z`;

/** -1 / 0 / 1 — for sorting or range checks (e.g. min/max date bounds) without a native `Date`. */
export const compareCalendarDates = (date1: CalendarDate, date2: CalendarDate): number =>
  Temporal.PlainDate.compare(date1, date2);

/** Builds a `CalendarDate` from plain year/month/day numbers; throws `RangeError` on an invalid date (e.g. Feb 30). */
export const calendarDateFromParts = (year: number, month: number, day: number): CalendarDate =>
  Temporal.PlainDate.from({ year, month, day }, { overflow: 'reject' });

/**
 * Interop with native `Date` — only for MUI date-picker / `date-fns` boundary
 * code, never for arithmetic. Reads the source `Date` using **local** getters
 * because MUI pickers emit and consume `Date` values in the browser's local
 * timezone (not UTC). Using UTC getters would shift the calendar day in
 * UTC-negative zones (e.g. `America/New_York`).
 */
export const calendarDateFromLocalDate = (date: Date): CalendarDate =>
  Temporal.PlainDate.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });

/**NEVER USE OUTSIDE DATE PICKER*/
export const calendarDateToLocalDate = (date: CalendarDate): Date => new Date(date.year, date.month - 1, date.day);
/**NEVER USE OUTSIDE DATE PICKER*/
export const dateTimeToLocalDate = (dateTime: DateTime): Date => new Date(dateTime.epochMilliseconds);

// ---------------------------------------------------------------------------
// Current date / time
// ---------------------------------------------------------------------------

/**
 * Today in the given timezone (defaults to the runtime's local timezone via
 * `Temporal.Now.timeZoneId()`).
 *
 * **Browser**: no argument needed — picks up the user's system timezone.
 * **Lambda**: always pass the building/lease timezone explicitly; Lambda
 * defaults to UTC, which will produce the wrong date for properties in
 * non-UTC zones (e.g. a midnight cron fired for an `'Australia/Sydney'`
 * property is still "yesterday" in UTC).
 */
export const today = (timeZone: string = Temporal.Now.timeZoneId()): CalendarDate =>
  Temporal.Now.plainDateISO(timeZone);

export const todayDateTime = (): DateTime => Temporal.Now.instant();

export const COPYRIGHT_YEAR = today().year;

export const getStartMonthOfThisYear = (startMonth: CalendarDate): CalendarDate =>
  startOfCalendarMonth(setCalendarDate(startMonth, today().year, 'year'));

// ---------------------------------------------------------------------------
// Arithmetic — CalendarDate
// ---------------------------------------------------------------------------

export const addCalendarDate = (date: CalendarDate, amount: number, unit: CalendarDateDurationUnit): CalendarDate =>
  date.add({ [unit]: amount });

export const subtractCalendarDate = (
  date: CalendarDate,
  amount: number,
  unit: CalendarDateDurationUnit
): CalendarDate => date.subtract({ [unit]: amount });

export const setCalendarDate = (date: CalendarDate, value: number, field: CalendarDateField): CalendarDate =>
  date.with({ [field]: value });

export const startOfCalendarMonth = (date: CalendarDate): CalendarDate => setCalendarDate(date, 1, 'day');

export const endOfCalendarMonth = (date: CalendarDate): CalendarDate => setCalendarDate(date, date.daysInMonth, 'day');

export const startOfCalendarYear = (date: CalendarDate): CalendarDate =>
  startOfCalendarMonth(setCalendarDate(date, 1, 'month'));

export const endOfCalendarYear = (date: CalendarDate): CalendarDate =>
  endOfCalendarMonth(setCalendarDate(date, 12, 'month'));

export const addCalendarBusinessDay = (date: CalendarDate, businessDays: number): CalendarDate => {
  if (businessDays <= 0) {
    return date;
  }
  const startDate =
    date.dayOfWeek > LAST_BUSINESS_DAY_OF_WEEK
      ? subtractCalendarDate(date, date.dayOfWeek - LAST_BUSINESS_DAY_OF_WEEK, 'days')
      : date;
  const dayIndex = startDate.dayOfWeek - 1; // 0 = Monday
  const weekendDays = WEEKEND_DAYS_PER_WEEK * Math.floor((dayIndex + businessDays) / BUSINESS_DAYS_PER_WEEK);
  return addCalendarDate(startDate, businessDays + weekendDays, 'days');
};

// ---------------------------------------------------------------------------
// Arithmetic — DateTime
// ---------------------------------------------------------------------------

export const addDateTime = (dateTime: DateTime, amount: number, unit: DateTimeDurationUnit): DateTime =>
  dateTime.add({ [unit]: amount });

export const getDateTimeDifference = ({ start, end }: DateTimeInterval, unit: DateTimeDurationUnit): number =>
  end.since(start).total({ unit });

// ---------------------------------------------------------------------------
// Comparisons — CalendarDate
// ---------------------------------------------------------------------------

export const isAfterOrEqual = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  Temporal.PlainDate.compare(date, otherDate) >= 0;

export const isAfter = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  Temporal.PlainDate.compare(date, otherDate) > 0;

export const isEqual = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  Temporal.PlainDate.compare(date, otherDate) === 0;

export const isEqualOrNull = (
  date: CalendarDate | null | undefined,
  otherDate: CalendarDate | null | undefined
): boolean => {
  if (isNil(date) || isNil(otherDate)) {
    return isNil(date) && isNil(otherDate);
  }
  return isEqual(date, otherDate);
};

export const isBefore = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  Temporal.PlainDate.compare(date, otherDate) < 0;

export const isBeforeOrEqual = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  Temporal.PlainDate.compare(date, otherDate) <= 0;

export const isSameMonthAndYear = (date: CalendarDate, otherDate: CalendarDate): boolean =>
  date.month === otherDate.month && date.year === otherDate.year;

// ---------------------------------------------------------------------------
// Comparisons — DateTime
// ---------------------------------------------------------------------------

export const isInstantBeforeOrEqual = (instant: DateTime, otherInstant: DateTime): boolean =>
  Temporal.Instant.compare(instant, otherInstant) <= 0;

export const isInstantAfterOrEqual = (instant: DateTime, otherInstant: DateTime): boolean =>
  Temporal.Instant.compare(instant, otherInstant) >= 0;

export const isInstantAfter = (instant: DateTime, otherInstant: DateTime): boolean =>
  Temporal.Instant.compare(instant, otherInstant) > 0;

export const isInstantBefore = (instant: DateTime, otherInstant: DateTime): boolean =>
  Temporal.Instant.compare(instant, otherInstant) < 0;

export const isInstantEqual = (instant: DateTime, otherInstant: DateTime): boolean =>
  Temporal.Instant.compare(instant, otherInstant) === 0;

// ---------------------------------------------------------------------------
// Intervals
// ---------------------------------------------------------------------------

const minCalendarDate = (dateA: CalendarDate, dateB: CalendarDate): CalendarDate =>
  isBefore(dateA, dateB) ? dateA : dateB;

const maxCalendarDate = (dateA: CalendarDate, dateB: CalendarDate): CalendarDate =>
  isAfter(dateA, dateB) ? dateA : dateB;

export const isStrictlyWithinInterval = (date: CalendarDate, interval: Interval): boolean =>
  isAfter(date, interval.start) && isBefore(date, interval.end);

export const isWithinInterval = (date: CalendarDate, interval: Interval): boolean =>
  isAfterOrEqual(date, interval.start) && isBeforeOrEqual(date, interval.end);

export const getNumberOfDaysInInterval = (interval: Interval, isInclusive = true): number =>
  interval.start.until(interval.end).days + (isInclusive ? 1 : 0);

export const getNumberOfYearsInInterval = (interval: Interval): number =>
  interval.start.until(interval.end, { largestUnit: 'years' }).years;

export const getNumberOfMonthsBetweenCalendarDates = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  inclusive = false
): number => (endDate.year - startDate.year) * 12 + (endDate.month - startDate.month) + (inclusive ? 1 : 0);

export const getNumberOfMonthsInInterval = (interval: Interval): number => {
  const startMonth = startOfCalendarMonth(interval.start);
  const endMonth = startOfCalendarMonth(interval.end);

  return Math.max(0, getNumberOfMonthsBetweenCalendarDates(startMonth, endMonth, true));
};

export const getMonthsInInterval = (interval: Interval): CalendarDate[] => {
  const startMonth = startOfCalendarMonth(interval.start);

  return Array.from({ length: getNumberOfMonthsInInterval(interval) }, (_, index) =>
    addCalendarDate(startMonth, index, 'months')
  );
};

export const getOverlappingDaysInIntervals = (
  intervalA: Interval,
  intervalB: Interval,
  isInclusive = false
): number => {
  const overlapStart = maxCalendarDate(intervalA.start, intervalB.start);
  const overlapEnd = minCalendarDate(intervalA.end, intervalB.end);

  if (isAfter(overlapStart, overlapEnd)) {
    return 0;
  }

  return getNumberOfDaysInInterval({ start: overlapStart, end: overlapEnd }, isInclusive);
};

export const areIntervalsOverlapping = (intervalA: Interval, intervalB: Interval): boolean =>
  isBefore(intervalA.start, intervalB.end) && isAfter(intervalA.end, intervalB.start);

// ---------------------------------------------------------------------------
// Quarter / Semester
// ---------------------------------------------------------------------------

export const getQuarterStartMonth = (date: CalendarDate): number =>
  Math.floor((date.month - 1) / MONTHS_IN_QUARTER) * MONTHS_IN_QUARTER + 1;

export const getQuarterNumber = (date: CalendarDate): number => Math.floor((date.month - 1) / MONTHS_IN_QUARTER) + 1;

export const getSemesterStartMonth = (date: CalendarDate): number =>
  Math.floor((date.month - 1) / MONTHS_IN_SEMESTER) * MONTHS_IN_SEMESTER + 1;

export const getSemesterNumber = (date: CalendarDate): number => Math.floor((date.month - 1) / MONTHS_IN_SEMESTER) + 1;

// ---------------------------------------------------------------------------
// Period ranges (semester / trimester / year)
// ---------------------------------------------------------------------------

export const getLastSemester = (date: CalendarDate): Interval => {
  const startOfCurrentSemester = date.with({
    month: getSemesterStartMonth(date),
    day: 1,
  });

  const start = startOfCurrentSemester.subtract({ months: MONTHS_IN_SEMESTER });
  const end = startOfCurrentSemester.subtract({ days: 1 });

  return { start, end };
};

export const getLastTrimester = (date: CalendarDate): Interval => {
  const startOfCurrentTrimester = date.with({
    month: getQuarterStartMonth(date),
    day: 1,
  });

  const start = startOfCurrentTrimester.subtract({ months: MONTHS_IN_QUARTER });
  const end = startOfCurrentTrimester.subtract({ days: 1 });

  return { start, end };
};

export const getLastYear = (date: CalendarDate): Interval => {
  const startOfCurrentYear = date.with({
    month: 1,
    day: 1,
  });

  return {
    start: startOfCurrentYear.subtract({ years: 1 }),
    end: startOfCurrentYear.subtract({ days: 1 }),
  };
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatCalendarDate = (date: CalendarDate, language: Language): string =>
  date.toLocaleString(getLocaleFromLanguage(language).code, { day: '2-digit', month: '2-digit', year: 'numeric' });

// en-GB abbreviates September as "Sept"; en-US keeps every short English month at three letters.
const SHORT_MONTH_LOCALE_OVERRIDES: Partial<Record<Language, string>> = { [Language.EN]: 'en-US' };

const MONTH_AND_YEAR_FORMAT_OPTIONS: Record<CalendarMonthFormat, Intl.DateTimeFormatOptions> = {
  numeric: { month: '2-digit', year: 'numeric' },
  short: { month: 'short', year: '2-digit' },
  long: { month: 'long', year: 'numeric' },
};

const getMonthLocaleCode = (language: Language, monthFormat: CalendarMonthFormat): string | undefined => {
  const shortMonthLocale = monthFormat === 'short' ? SHORT_MONTH_LOCALE_OVERRIDES[language] : undefined;
  return shortMonthLocale ?? getLocaleFromLanguage(language).code;
};

export const formatCalendarMonth = (
  date: CalendarDate,
  language: Language,
  monthFormat: Exclude<CalendarMonthFormat, 'numeric'> = 'long'
): string => date.toLocaleString(getMonthLocaleCode(language, monthFormat), { month: monthFormat });

export const formatCalendarMonthAndYear = (
  date: CalendarDate,
  language: Language,
  monthFormat: CalendarMonthFormat = 'numeric'
): string => date.toLocaleString(getMonthLocaleCode(language, monthFormat), MONTH_AND_YEAR_FORMAT_OPTIONS[monthFormat]);

// Shared core: signedSeconds is (start - end), negative when start is in the past → "X ago".
const formatRelativeFromSignedSeconds = (signedSeconds: number, language: Language, allowSubDay: boolean): string => {
  const oneDay = 24 * 60 * 60;
  const thresholds: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: 'year', seconds: 365 * oneDay },
    { unit: 'month', seconds: 30 * oneDay },
    { unit: 'day', seconds: oneDay },
    ...(allowSubDay
      ? ([
          { unit: 'hour', seconds: 60 * 60 },
          { unit: 'minute', seconds: 60 },
        ] as { unit: Intl.RelativeTimeFormatUnit; seconds: number }[])
      : []),
  ];
  const formatter = new Intl.RelativeTimeFormat(getLocaleFromLanguage(language).code, { numeric: 'auto' });
  const threshold = thresholds.find(({ seconds }) => Math.abs(signedSeconds) >= seconds);
  if (threshold === undefined) {
    return allowSubDay ? formatter.format(Math.trunc(signedSeconds), 'second') : formatter.format(0, 'day');
  }
  return formatter.format(Math.trunc(signedSeconds / threshold.seconds), threshold.unit);
};

// Relative time of interval.start as seen from interval.end (e.g. "3 days ago", "in 2 hours").
export const formatRelativeDateTime = (interval: DateTimeInterval, language: Language): string =>
  formatRelativeFromSignedSeconds(-getDateTimeDifference(interval, 'seconds'), language, true);

// Relative calendar distance of interval.start as seen from interval.end (e.g. "3 days ago", "in 2 months").
export const formatRelativeCalendarDate = (interval: Interval, language: Language): string =>
  formatRelativeFromSignedSeconds(-getNumberOfDaysInInterval(interval, false) * 24 * 60 * 60, language, false);

const dayMonthFormatOptions = (withYear: boolean): Intl.DateTimeFormatOptions => ({
  day: 'numeric',
  month: 'short',
  ...(withYear ? { year: 'numeric' } : {}),
});

export const formatCalendarDateDayMonth = (date: CalendarDate, language: Language, withYear = true): string =>
  date.toLocaleString(getLocaleFromLanguage(language).code, dayMonthFormatOptions(withYear));

export const formatDateTimeDayMonth = (dateTime: DateTime, language: Language, withYear = true): string =>
  dateTime.toLocaleString(getLocaleFromLanguage(language).code, {
    timeZone: DEFAULT_TIME_ZONE,
    ...dayMonthFormatOptions(withYear),
  });

export const formatDateTimeHourAndMinute = (dateTime: DateTime, language: Language): string =>
  dateTime.toLocaleString(getLocaleFromLanguage(language).code, {
    timeZone: DEFAULT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  });

export const formatDateTimeDayAndTime = (dateTime: DateTime, language: Language, todayLabel: string): string => {
  const day = isEqual(calendarDateFromInstant(dateTime), today(DEFAULT_TIME_ZONE))
    ? todayLabel
    : formatDateTimeDayMonth(dateTime, language, false);
  return `${day} ${formatDateTimeHourAndMinute(dateTime, language)}`;
};

export const formatDateTimeLongDate = (dateTime: DateTime, language: Language): string =>
  dateTime.toLocaleString(getLocaleFromLanguage(language).code, {
    timeZone: DEFAULT_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const formatDateRange = (period: { from?: CalendarDate; to?: CalendarDate }, language: Language) => {
  const fromLabel = period.from ? formatCalendarDate(period.from, language) : '…';
  const toLabel = period.to ? formatCalendarDate(period.to, language) : '…';
  return `${fromLabel} – ${toLabel}`;
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

// Validity check that supports both legacy JS Date (from MUI pickers) and Temporal types.
// Temporal.PlainDate/Temporal.Instant can never be in an invalid state once constructed, so only a
// JS Date can be "Invalid Date" (NaN time).
export const isDateValid = (date: Date | CalendarDate | DateTime | null | undefined): boolean => {
  if (date === null || date === undefined) {
    return false;
  }
  if (date instanceof Date) {
    return !isNaN(date.getTime());
  }
  return true;
};
